import { PatientAccessCallAnswers } from '@bariendo/lib/patient-access-note';
import { PacAction, PacBeat, PacBeatContext, PacWidget } from '@bariendo/lib/patient-access-script';
import {
  PN_CARD_TRACK_LABELS,
  PN_CARD_TRACKS,
  PnCardTrack,
  PnV2SectionId,
} from '@bariendo/lib/pn-consultation-v2-script';
import { Anchor, Box, Button, Checkbox, Group, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { ReactNode, useEffect, useState } from 'react';
import { Chips, PatientAccessCallWidget, QsaRow } from './PatientAccessCallWidgets';
import { Track } from '@bariendo/lib/constants';
import { PacColumnsLayout, PacLayoutContext, QSA_GRID, sectionHasScript } from './pacLayout';

const HAIRLINE = '1px solid var(--mantine-color-gray-2)';

/** The section Save to chart opens when there is no outcome yet. */
const OUTCOME_SECTION_ID: PnV2SectionId = 'outcome';

/** The track row's chips sit on the left of their columns, as every v2.0 answer does. */
const TRACK_ROW_LAYOUT: PacColumnsLayout = { showScript: false, scripted: false };

/**
 * What saving does to the patient's track (BAR-2995): a switch, with the Close
 * acknowledgement when it needs one, or why it won't switch.
 */
export type TrackSwitchOnSave =
  | { to: Track; label: string; acknowledgement?: string }
  | { blocked: string }
  /** Not known yet: Save to chart waits. */
  | { waiting: string };

/** A link in a v2.0 section's answer column: "Switch to Insurance" (BAR-2995). */
export function QsaRowLink({
  onClick,
  testId,
  children,
}: {
  onClick: () => void;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <QsaRow>
      <Anchor component="button" type="button" size="sm" c="green.9" onClick={onClick} data-testid={testId}>
        {children}
      </Anchor>
    </QsaRow>
  );
}

interface PnConsultationV2BodyProps<T extends PatientAccessCallAnswers> {
  /** The six sections, built for the track the card shows. */
  script: readonly PacBeat[];
  answers: T;
  context?: PacBeatContext;
  setValue: (id: string, value: string | string[] | boolean | undefined) => void;
  onAction: (action: PacAction) => void;
  pendingAction?: PacAction;
  /** Actions this viewer cannot take; their links show disabled. */
  unavailableActions?: readonly PacAction[];
  renderCardWidget: (widget: PacWidget) => ReactNode;
  /** The chip lit, or undefined while the patient's track is still being read. */
  track: PnCardTrack | undefined;
  onTrackChange: (track: PnCardTrack) => void;
  /** ⋯ → Hide script: every section drops its Script column. */
  hideScript: boolean;
  readOnly: boolean;
  /** Saved to the chart and reopened (BAR-2996): still editable, no Save to chart. */
  charted?: boolean;
  isSaving: boolean;
  saveError: boolean;
  /** Save to chart can't run yet: there is no outcome. Clicking it opens Outcome instead. */
  saveBlocked?: boolean;
  /** What saving does to the patient's track, said above Save to chart (BAR-2995). */
  trackSwitch?: TrackSwitchOnSave;
  /** `acknowledged`: the track switch's acknowledgement, when it needs one. */
  onSave: (acknowledged: boolean) => void;
  /** The sections open, so the card can defer work until a section is on screen. */
  onOpenSectionsChange?: (open: ReadonlySet<string>) => void;
}

/**
 * The PN consultation card v2.0's body (BAR-2994): the Track row, then six
 * sections, title and chevron only, each opening on its own. An open section is
 * Question · Script · Answer on three columns.
 *
 * A body of its own rather than a mode of `PatientAccessCallCard`: that spine is
 * the shipped layout for every call card (one beat open at a time, a summary
 * and a tick per beat, gray-out rules), and none of it carries over. The
 * widgets do: every row renders through `PatientAccessCallWidget`, laid out by
 * `PacLayoutContext`.
 */
export function PnConsultationV2Body<T extends PatientAccessCallAnswers>({
  script,
  answers,
  context,
  setValue,
  onAction,
  pendingAction,
  unavailableActions,
  renderCardWidget,
  track,
  onTrackChange,
  hideScript,
  readOnly,
  charted = false,
  isSaving,
  saveError,
  saveBlocked = false,
  trackSwitch,
  onSave,
  onOpenSectionsChange,
}: PnConsultationV2BodyProps<T>) {
  // Every section starts closed, and each opens and closes on its own.
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  // Save to chart was clicked with no outcome; cleared once there is one.
  const [askedForOutcome, setAskedForOutcome] = useState(false);
  // A charted note with no outcome (a track switch cleared it) says so: it
  // has no Save to chart to ask on.
  const showOutcomeNeeded = saveBlocked && (askedForOutcome || charted);

  const setOpenSections = (next: Set<string>) => {
    setOpen(next);
    onOpenSectionsChange?.(next);
  };

  const toggle = (id: string) => {
    const next = new Set(open);
    if (!next.delete(id)) next.add(id);
    setOpenSections(next);
  };

  const [acknowledged, setAcknowledged] = useState(false);
  const needsAcknowledgement = Boolean(trackSwitch && 'to' in trackSwitch && trackSwitch.acknowledgement);
  // An acknowledgement is for one move: a different target asks again.
  const switchTarget = trackSwitch && 'to' in trackSwitch ? trackSwitch.to : undefined;
  const waitingOnTrack = Boolean(trackSwitch && 'waiting' in trackSwitch);
  useEffect(() => setAcknowledged(false), [switchTarget]);

  const save = () => {
    if (!saveBlocked) {
      if (needsAcknowledgement && !acknowledged) return;
      onSave(acknowledged);
      return;
    }
    // Opens Outcome rather than refusing silently: the outcome is what the save needs.
    setAskedForOutcome(true);
    if (!open.has(OUTCOME_SECTION_ID)) setOpenSections(new Set([...open, OUTCOME_SECTION_ID]));
  };

  return (
    <Box>
      <PacLayoutContext.Provider value={TRACK_ROW_LAYOUT}>
        <Box py={8} style={{ ...QSA_GRID, alignItems: 'center', borderBottom: HAIRLINE, minHeight: 44 }}>
          <Text size="xs" c="dimmed">
            Track
          </Text>
          <Box style={{ gridColumn: '2 / span 2' }}>
            <Chips
              options={PN_CARD_TRACKS}
              labels={PN_CARD_TRACK_LABELS}
              selected={track ? [track] : []}
              // Not while a save runs: it switches to the track it started with.
              disabled={isSaving}
              // A saved note's chips switch only what it shows; the card writes nothing.
              onToggle={(option) => {
                if (option !== track) onTrackChange(option as PnCardTrack);
              }}
            />
          </Box>
        </Box>
      </PacLayoutContext.Provider>

      {script.map((beat) => {
        const isOpen = open.has(beat.id);
        const scripted = sectionHasScript(beat);
        const showScript = scripted && !hideScript;
        return (
          <Box key={beat.id} style={{ borderBottom: HAIRLINE }}>
            <UnstyledButton w="100%" py={10} aria-expanded={isOpen} onClick={() => toggle(beat.id)}>
              <Group gap={10} wrap="nowrap">
                <Text size="sm" fw={600} style={{ flex: 1, minWidth: 0 }} truncate>
                  {beat.title}
                </Text>
                <IconChevronDown
                  size={14}
                  style={{
                    flexShrink: 0,
                    transition: 'transform 150ms ease',
                    transform: isOpen ? 'rotate(180deg)' : 'none',
                  }}
                />
              </Group>
            </UnstyledButton>

            {isOpen && (
              <PacLayoutContext.Provider value={{ showScript: !hideScript, scripted }}>
                <Box pb={4} data-testid={`pn-section-${beat.id}`}>
                  <Box py={6} style={QSA_GRID}>
                    <Text size="xs" c="dimmed">
                      Question
                    </Text>
                    {showScript && (
                      <Text size="xs" c="dimmed" data-testid="pn-script-column">
                        Script
                      </Text>
                    )}
                    <Text size="xs" c="dimmed" style={showScript ? undefined : { gridColumn: '2 / span 2' }}>
                      Answer
                    </Text>
                  </Box>
                  {beat.items.map((widget, index) => (
                    <PatientAccessCallWidget
                      key={index}
                      widget={widget}
                      answers={answers}
                      context={context}
                      setValue={setValue}
                      onAction={onAction}
                      pendingAction={pendingAction}
                      unavailableActions={unavailableActions}
                      disabled={readOnly}
                      renderCardWidget={renderCardWidget}
                    />
                  ))}
                </Box>
              </PacLayoutContext.Provider>
            )}
          </Box>
        );
      })}

      {trackSwitch && !readOnly && !charted && (
        <Box pt={10} data-testid="pn-track-switch">
          <Text size="xs" c="dimmed">
            {'to' in trackSwitch
              ? trackSwitch.label
              : 'blocked' in trackSwitch
                ? trackSwitch.blocked
                : trackSwitch.waiting}
          </Text>
          {'to' in trackSwitch && trackSwitch.acknowledgement && (
            <Checkbox
              size="xs"
              mt={6}
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.currentTarget.checked)}
              label={<Text size="xs">{trackSwitch.acknowledgement}</Text>}
              data-testid="pn-track-switch-acknowledge"
            />
          )}
        </Box>
      )}
      <Group justify="space-between" py={10}>
        <Text size="xs" c={saveError || showOutcomeNeeded ? 'red.7' : 'dimmed'} data-testid="pn-save-status">
          {saveError
            ? "Couldn't autosave — your answers are still here"
            : showOutcomeNeeded
              ? 'Record an outcome first'
              : readOnly || charted
                ? 'Saved to chart'
                : ''}
        </Text>
        {!readOnly && !charted && (
          <Button
            size="compact-sm"
            color="green.9"
            loading={isSaving}
            // A switch that scrubs a Close record waits for its acknowledgement.
            // The outcome is asked for first: with none, the click opens Outcome.
            disabled={(!saveBlocked && needsAcknowledgement && !acknowledged) || waitingOnTrack}
            onClick={save}
            data-testid="pn-save-to-chart"
          >
            Save to chart
          </Button>
        )}
      </Group>
    </Box>
  );
}
